import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { fetchAllCycles, type EPRSetupCycle } from '../services/eprSetupService';
import { listInvitationsForCycle, tallyInvitations } from '../services/reviewInvitationService';
import type { ReviewInvitation } from '../types/magicLink';

interface FormRow {
  id: string;
  revieweeId: string;
  revieweeName: string;
  cycleId: string;
  status: string;
  framework?: string;
  invitations: ReviewInvitation[];
}

const STATUS_TONE: Record<string, string> = {
  prep_window:       'bg-stone-100 text-stone-600',
  self_pending:      'bg-blue-50 text-blue-700',
  self_submitted:    'bg-blue-100 text-blue-800',
  '360_pending':     'bg-purple-50 text-purple-700',
  '360_collected':   'bg-purple-100 text-purple-800',
  manager_pending:   'bg-amber-50 text-amber-700',
  manager_submitted: 'bg-amber-100 text-amber-800',
  calibration:       'bg-indigo-50 text-indigo-700',
  approved:          'bg-emerald-50 text-emerald-700',
  signed_off:        'bg-emerald-100 text-emerald-800',
};

const ReviewsIndex: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cycles, setCycles] = useState<EPRSetupCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [forms, setForms] = useState<FormRow[]>([]);

  // Initial cycle load
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const all = await fetchAllCycles();
        if (!alive) return;
        const active = all.filter(c => c.status === 'active' || c.status === 'draft');
        setCycles(active);
        if (active.length > 0 && !selectedCycleId) setSelectedCycleId(active[0].id);
      } catch (e) {
        setError(String((e as Error).message ?? e));
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch forms + invitations for chosen cycle
  useEffect(() => {
    if (!selectedCycleId) return;
    let alive = true;
    void (async () => {
      try {
        setLoading(true);
        const [formsSnap, invs] = await Promise.all([
          getDocs(query(collection(db, 'reviewForms'), where('cycleId', '==', selectedCycleId))),
          listInvitationsForCycle(selectedCycleId),
        ]);
        if (!alive) return;
        const invsByForm: Record<string, ReviewInvitation[]> = {};
        for (const i of invs) {
          (invsByForm[i.reviewFormId] ??= []).push(i);
        }
        const rows: FormRow[] = formsSnap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            revieweeId:   String(data.revieweeId ?? ''),
            revieweeName: String(data.revieweeName ?? ''),
            cycleId:      String(data.cycleId ?? ''),
            status:       String(data.status ?? '—'),
            framework:    data.framework,
            invitations:  invsByForm[d.id] ?? [],
          };
        }).sort((a, b) => a.revieweeName.localeCompare(b.revieweeName));
        setForms(rows);
        setLoading(false);
      } catch (e) {
        setError(String((e as Error).message ?? e));
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedCycleId]);

  const totals = useMemo(() => {
    const allInvs = forms.flatMap(f => f.invitations);
    return tallyInvitations(allInvs);
  }, [forms]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500">HR Workflow</div>
          <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">Reviews</h1>
          <p className="text-sm text-stone-600 max-w-xl mt-1">
            All reviewees in the selected cycle. Click a row to monitor reviewer status &amp; aggregated scores.
          </p>
        </div>
        <Link
          to="/reviews/new"
          className="px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-md hover:bg-stone-800"
        >
          + Start a review
        </Link>
      </div>

      {/* Cycle selector */}
      <div className="flex items-center gap-2 text-sm">
        <label className="text-stone-500">Cycle:</label>
        <select
          value={selectedCycleId}
          onChange={e => setSelectedCycleId(e.target.value)}
          className="px-3 py-1.5 border border-stone-300 rounded-md text-sm bg-white"
        >
          {cycles.length === 0 && <option>— No active cycles —</option>}
          {cycles.map(c => (
            <option key={c.id} value={c.id}>{c.name} {c.status === 'draft' ? '(draft)' : ''}</option>
          ))}
        </select>
      </div>

      {/* Summary */}
      {!loading && forms.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card label="Reviewees" value={forms.length} sub="in this cycle" />
          <Card label="Reviewers invited" value={totals.total} sub="total magic links" />
          <Card label="Submitted" value={totals.submitted} sub={`${Math.round(totals.submissionRate * 100)}% complete`} tone="ok" />
          <Card label="Pending" value={totals.sent + totals.opened + totals.inProgress} sub="awaiting submit" tone={totals.sent + totals.opened + totals.inProgress > 0 ? 'warn' : 'neutral'} />
        </div>
      )}

      {/* Forms table */}
      <div className="bg-white border border-stone-200 rounded-xl">
        {loading ? (
          <div className="p-8 text-center text-stone-500 text-sm">Loading reviews…</div>
        ) : error ? (
          <div className="p-4 bg-red-50 border-b border-red-200 text-sm text-red-800">{error}</div>
        ) : forms.length === 0 ? (
          <div className="p-8 text-center text-stone-500 text-sm">
            No reviews yet for this cycle.{' '}
            <Link to="/reviews/new" className="text-blue-600 hover:underline">Start one →</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] text-stone-500 uppercase tracking-wider border-b border-stone-200">
                <th className="px-4 py-2.5">Reviewee</th>
                <th className="px-4 py-2.5">Workflow status</th>
                <th className="px-4 py-2.5">Reviewers</th>
                <th className="px-4 py-2.5">Submitted</th>
                <th className="px-4 py-2.5">Framework</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {forms.map(f => {
                const stats = tallyInvitations(f.invitations);
                const pct = stats.total ? Math.round(stats.submissionRate * 100) : 0;
                return (
                  <tr key={f.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-stone-900">{f.revieweeName}</div>
                      <div className="text-[11px] text-stone-500 font-mono">{f.id}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_TONE[f.status] ?? 'bg-stone-100 text-stone-600'}`}>
                        {f.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums">{stats.total}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="tabular-nums">{stats.submitted}/{stats.total}</span>
                        <div className="w-16 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                          <div
                            className={pct >= 80 ? 'h-full bg-emerald-500' : pct >= 50 ? 'h-full bg-amber-500' : 'h-full bg-red-400'}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[11px] text-stone-500">{f.framework ?? 'legacy'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Link to={`/reviews/${f.id}`} className="text-blue-600 hover:underline text-sm">
                        Monitor →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Card: React.FC<{ label: string; value: number | string; sub: string; tone?: 'ok' | 'warn' | 'neutral' }> = ({ label, value, sub, tone = 'neutral' }) => {
  const accent = tone === 'ok' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : 'text-stone-900';
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${accent}`}>{value}</div>
      <div className="text-[11px] text-stone-500">{sub}</div>
    </div>
  );
};

export default ReviewsIndex;
